<script setup lang="ts">
import { computed, ref, reactive, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useGlobalStore } from '@/pinia/GlobalStore'
import { useNotificationStore } from '@/pinia/NotificationStore'
import { closeDialog } from 'vue3-promise-dialog'

import { type IKeyValueMulitTypeStore } from '@/types'

export interface Props {
  open: boolean
}

const isSaving = ref(false)
const notificationStore = useNotificationStore()

defineProps<Props>()
const globalStore = useGlobalStore()

const { user, params } = storeToRefs(globalStore)

const form = reactive(user)
const formRef = ref()
const errors = computed(() => formRef.value?.errors || null)
const countryId = ref(0)
const error = ref('')

const onClose = () => {
  closeDialog(true)
}

const vatRule = computed(() => {
  const country = params.value.countries.find(item => item.id === countryId.value)
  if (country) {
    return `vat_id:${country.iso_code}:${country.name}:old_vat`
  }
  return ''
})

onMounted(async () => {
  await globalStore.loadParams(['salutations', 'countries'])
  countryId.value = user.value.profile.country_id
  formRef.value.setFieldValue('old_vat', user.value.vat_id)
})

const onCountryChange = (value: string) => {
  countryId.value = parseInt(value)
}

const onAvatarChanged = (value: string) => {
  formRef.value.setFieldValue('avatar', value)
}

const onSuccess = async (values: IKeyValueMulitTypeStore) => {
  try {
    await globalStore.updateUserProfile(values)
    notificationStore.addClientNotification(
      {
        title: 'Profil ändern',
        text: 'Dein Profil wurde erfolgreich geändert.',
        variant: 'success'
      })
    closeDialog(true)
  } catch (error) {
    console.error(error)
  }
}

</script>
<template>
  <storm-ui-dialog
    :show="open"
    height="auto"
    width="lg"
    title="Profil bearbeiten"
    @hide="onClose"
  >
    <template #content>
      <storm-ui-form-errors
        :errors="errors"
      />
      <storm-ui-alert
        v-show="error"
        title="Ups, das hat nicht funktioniert"
      >
        <p>{{ error }}</p>
      </storm-ui-alert>

      <storm-ui-form
        v-if="params.salutations && params.countries"
        id="profil-form"
        ref="formRef"
        :initial-values="form"
        @success="onSuccess"
        @request="isSaving = true"
      >
        <div class="grid grid-cols-2 mt-3">
          <div class="col-span-1">
            <storm-ui-form-group title="Vertragspartner">
              <div class="col-span-24 flex items-center justify-center">
                <storm-ui-avatar-upload
                  class="mb-2"
                  :size="16"
                  :src="user.avatar_url"
                  @avatar-changed="onAvatarChanged"
                />
              </div>
              <div class="col-span-6">
                <storm-ui-select
                  name="salutation_id"
                  placeholder="Anrede"
                  rules="required"
                  label="Anrede"
                  :options="params.salutations"
                />
              </div>
              <div class="col-span-8">
                <storm-ui-input
                  name="first_name"
                  rules="required"
                  label="Vorname"
                />
              </div>
              <div class="col-span-10">
                <storm-ui-input
                  name="last_name"
                  rules="required"
                  label="Nachname"
                />
              </div>
              <div class="col-span-24">
                <storm-ui-input
                  name="company"
                  label="Firma"
                />
              </div>
            </storm-ui-form-group>
            <storm-ui-form-group title="Kontaktdaten">
              <div class="col-span-24">
                <storm-ui-input
                  readonly
                  name="email"
                  label="E-Mail-Adresse"
                >
                  Bitte wende Dich an unser Support-Team, wenn sich Deine E-Mail-Adresse geändert hat.
                </storm-ui-input>
              </div>
              <div class="col-span-12">
                <storm-ui-input
                  name="profile.phone"
                  rules="required"
                  label="Telefon"
                />
              </div>
            </storm-ui-form-group>
          </div>
          <div class="col-span-1">
            <storm-ui-form-group title="Rechnungsanschrift">
              <div class="col-span-24">
                <storm-ui-input
                  name="profile.street"
                  label="Straße"
                  rules="required"
                />
              </div>
              <div class="col-span-6">
                <storm-ui-input
                  name="profile.zip"
                  label="PLZ"
                  rules="required"
                />
              </div>
              <div class="col-span-18">
                <storm-ui-input
                  name="profile.city"
                  label="Ort"
                  rules="required"
                />
              </div>
              <div class="col-span-24">
                <storm-ui-select
                  name="profile.country_id"
                  label="Land"
                  rules="required"
                  :options="params.countries"
                  @input="onCountryChange"
                />
              </div>
            </storm-ui-form-group>
            <storm-ui-form-group
              v-if="[1, 112].includes(countryId)"
              title="Umsatzsteuer"
            >
              <div
                class="col-span-24"
              >
                <storm-ui-input
                  name="vat_id"
                  mode="passive"
                  label="USt-IdNr."
                  :rules="vatRule"
                >
                  Falls Du nicht in Österreich steuerpflichtig bist, kannst Du hier Deine USt-IdNr. angeben.
                </storm-ui-input>
              </div>
            </storm-ui-form-group>
            <storm-ui-input
              name="avatar"
              type="hidden"
            />
          </div>
        </div>
      </storm-ui-form>
    </template>
    <template #footer>
      <div class="flex-1 flex items-center px-4">
        <div class="flex-1 space-x-2 text-right">
          <storm-ui-button
            variant="link"
            type="button"
            @click="onClose"
          >
            Abbrechen
          </storm-ui-button>
          <storm-ui-button
            type="submit"
            form="profil-form"
            variant="primary"
          >
            Speichern
          </storm-ui-button>
        </div>
      </div>
    </template>
  </storm-ui-dialog>
</template>
